import React, { useState, useEffect, useMemo } from 'react';
import { getFullApiUrl } from '../config';
import config from '../config';

const AuditPlanUploadSection = ({ onProcessingComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [reportType, setReportType] = useState('report1');
  const [reportTypes, setReportTypes] = useState([]); // Initialize as empty array
  
  // Create safeReportTypes using useMemo to ensure it's always an array and reactive to changes
  const safeReportTypes = useMemo(() => {
    return Array.isArray(reportTypes) ? reportTypes : [];
  }, [reportTypes]);

  // Load report types on component mount
  useEffect(() => {
    fetchReportTypes();
  }, []);

  const fetchReportTypes = async () => {
    try {
      const response = await fetch(getFullApiUrl('AUDIT_PLAN_REPORT_TYPES'));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Report types loaded:', data.report_types);
      // Ensure reportTypes is always an array, never undefined or null
      const types = data?.report_types;
      if (Array.isArray(types) && types.length > 0) {
        setReportTypes(types);
      } else {
        // If API returns empty or invalid data, use fallback
        console.warn('Report types from API is empty or invalid, using fallback');
        setReportTypes([
          {
            "value": "report1",
            "label": "Report 1 - Regulatory Analysis",
            "description": "Analyze OJK/BI regulatory documents (POJK, PBI, PADG) to extract audit requirements and technology security testing",
            "document_types": "POJK, PBI, PADG",
            "output": "Two tables: Audit Wajib Tahunan & Area Teknologi dan Pengujian Keamanan",
            "use_case": "For regulatory compliance and audit planning"
          },
          {
            "value": "report2",
            "label": "Report 2 - High Risk Process Analysis", 
            "description": "Analyze internal documents (SOP, WI, Policy, Guidelines) to identify high-risk processes",
            "document_types": "SOP, WI, Policy, Guidelines",
            "output": "Table of high-risk processes with impact analysis and controls",
            "use_case": "For operational risk assessment and process improvement"
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching report types:', error);
      console.log('Using fallback report types data');
      // Fallback data if API is not available - ensure it's always an array
      setReportTypes([
        {
          "value": "report1",
          "label": "Report 1 - Regulatory Analysis",
          "description": "Analyze OJK/BI regulatory documents (POJK, PBI, PADG) to extract audit requirements and technology security testing",
          "document_types": "POJK, PBI, PADG",
          "output": "Two tables: Audit Wajib Tahunan & Area Teknologi dan Pengujian Keamanan",
          "use_case": "For regulatory compliance and audit planning"
        },
        {
          "value": "report2",
          "label": "Report 2 - High Risk Process Analysis", 
          "description": "Analyze internal documents (SOP, WI, Policy, Guidelines) to identify high-risk processes",
          "document_types": "SOP, WI, Policy, Guidelines",
          "output": "Table of high-risk processes with impact analysis and controls",
          "use_case": "For operational risk assessment and process improvement"
        }
      ]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(files); // Support multiple files
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files); // Support multiple files
    }
  };

  const updateProcessingStep = (step, status = 'processing') => {
    setProcessingSteps(prev => {
      const updated = [...prev];
      const existingIndex = updated.findIndex(s => s.name === step);
      if (existingIndex >= 0) {
        updated[existingIndex].status = status;
      } else {
        updated.push({ name: step, status });
      }
      return updated;
    });
  };

  const handleProcess = async () => {
    if (selectedFiles.length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Please select a file to analyze.'
      });
      return;
    }

    // Validate file types for all files
    const supportedTypes = ['.pdf', '.doc', '.docx', '.txt'];
    const invalidFiles = selectedFiles.filter(file => 
      !supportedTypes.some(type => file.name.toLowerCase().endsWith(type))
    );
    
    if (invalidFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: `File type not supported: ${invalidFiles[0].name}. Please select PDF, DOC, DOCX, or TXT files.`
      });
      return;
    }

    // Validate file sizes (50MB limit per file)
    const oversizedFiles = selectedFiles.filter(file => file.size > config.MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: `File size too large: ${oversizedFiles[0].name}. Maximum size is 50MB per file.`
      });
      return;
    }

    setIsProcessing(true);
    setProcessingSteps([]);
    
    // Use safeReportTypes defined at component level
    const selectedReportType = safeReportTypes.length > 0 
      ? safeReportTypes.find(type => type && type.value === reportType) 
      : null;
    setUploadStatus({
      type: 'info',
      message: `Starting ${selectedReportType?.label || 'audit'} analysis for ${selectedFiles.length} file(s)...`
    });

    try {
      const formData = new FormData();
      // Add all files to form data
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('report_type', reportType);

      // Show processing steps
      updateProcessingStep('File Upload', 'processing');
      updateProcessingStep('Pemrosesan dokumen', 'pending');
      updateProcessingStep('Document Analysis', 'pending');
      updateProcessingStep('Table Generation', 'pending');
      updateProcessingStep('Response Formatting', 'pending');

      const response = await fetch(getFullApiUrl('AUDIT_PLAN_ANALYZE'), {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        updateProcessingStep('File Upload', 'completed');
        updateProcessingStep('Pemrosesan dokumen', 'completed');
        updateProcessingStep('Document Analysis', 'completed');
        updateProcessingStep('Table Generation', 'completed');
        updateProcessingStep('Response Formatting', 'completed');

        setUploadStatus({
          type: 'success',
          message: `Successfully completed ${selectedReportType?.label || 'audit'} analysis!`
        });

        if (onProcessingComplete) {
          onProcessingComplete({
            ...result,
            report_type_info: selectedReportType
          });
        }
      } else {
        throw new Error(result.detail || result.message || 'Failed to process audit analysis');
      }
    } catch (error) {
      console.error('Audit analysis error:', error);
      updateProcessingStep('File Upload', 'error');
      setUploadStatus({
        type: 'error',
        message: `Error: ${error.message}`
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed': return 'fa-check-circle text-green-500';
      case 'processing': return 'fa-spinner fa-spin text-blue-500';
      case 'error': return 'fa-exclamation-triangle text-red-500';
      default: return 'fa-clock text-gray-400';
    }
  };

  // Find selected report type safely using safeReportTypes defined at component level
  const selectedReportType = safeReportTypes.length > 0 
    ? safeReportTypes.find(type => type && type.value === reportType) 
    : null;

  // Debug logging
  console.log('Current reportTypes:', reportTypes);
  console.log('Current reportType:', reportType);
  console.log('Selected report type:', selectedReportType);

  return (
    <section className="mb-8">
      {/* Report Type Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
          <i className="fa-solid fa-file-chart-line mr-2 text-purple-600 dark:text-purple-400"></i>
          Select Report Type
        </h4>
        <div className="grid grid-cols-1 gap-4">
          {safeReportTypes.length === 0 ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 dark:border-purple-400 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400 transition-colors duration-300">Loading report types...</p>
            </div>
          ) : (
            safeReportTypes.map((type) => (
            <div
              key={type.value}
              className={`p-5 border rounded-lg cursor-pointer transition-all ${
                reportType === type.value
                  ? 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30 shadow-md'
                  : 'border-gray-200 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-500 hover:shadow-sm'
              }`}
              onClick={() => setReportType(type.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="reportType"
                  value={type.value}
                  checked={reportType === type.value}
                  onChange={(e) => setReportType(e.target.value)}
                  className="mr-4 mt-1 text-purple-600 dark:text-purple-400"
                />
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h5 className="font-semibold text-gray-800 dark:text-white text-lg transition-colors duration-300">{type.label}</h5>
                    <span className={`ml-3 px-2 py-1 text-xs rounded-full font-medium transition-colors duration-300 ${
                      type.value === 'report1' 
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                    }`}>
                      {type.value === 'report1' ? 'Regulatory' : 'Risk Analysis'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 transition-colors duration-300">{type.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Document Types:</span>
                      <div className="text-gray-600 dark:text-gray-400 mt-1 transition-colors duration-300">{type.document_types}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Output:</span>
                      <div className="text-gray-600 dark:text-gray-400 mt-1 transition-colors duration-300">{type.output}</div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300 transition-colors duration-300">Use Case:</span>
                      <div className="text-gray-600 dark:text-gray-400 mt-1 transition-colors duration-300">{type.use_case}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            ))
          )}
        </div>
      </div>

      {/* File Upload Section */}
      <div 
        className={`bg-white dark:bg-gray-800 rounded-lg border border-dashed p-8 text-center transition-colors mb-6 ${
          isDragOver 
            ? 'border-purple-400 dark:border-purple-500 bg-purple-50 dark:bg-purple-900/30' 
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="max-w-md mx-auto">
          <div className="text-purple-500 dark:text-purple-400 mb-4 transition-colors duration-300">
            <i className="fa-solid fa-clipboard-check text-4xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">
            Audit Plan Analysis
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-300 mb-6 transition-colors duration-300">
            Upload one or multiple regulatory/internal documents for audit analysis
          </p>
          <label htmlFor="audit-file-upload" className="cursor-pointer">
            <span className={`font-medium py-2.5 px-5 rounded-lg inline-flex items-center ${
              isProcessing 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}>
              <i className="fa-solid fa-upload mr-2"></i>
              {selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : 'Select Documents'}
            </span>
            <input
              id="audit-file-upload"
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileSelect}
              disabled={isProcessing}
            />
          </label>
          
          <div className="mt-4 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <i className="fa-solid fa-robot mr-2 text-purple-500 dark:text-purple-400"></i>
            <span>Diproses dengan teknologi AI terenkripsi</span>
          </div>
        </div>
      </div>

      {/* Selected Files Display */}
      {selectedFiles.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-4 mb-6 transition-colors duration-300">
          <h4 className="text-sm font-medium text-gray-800 dark:text-white mb-3 transition-colors duration-300">
            Selected Documents ({selectedFiles.length}):
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <i className="fa-solid fa-file mr-2 text-gray-400 dark:text-gray-500"></i>
                <span className="flex-1 truncate">{file.name}</span>
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            ))}
          </div>
          {selectedReportType && (
            <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg transition-colors duration-300">
              <div className="text-sm font-medium text-purple-800 dark:text-purple-300 transition-colors duration-300">
                {selectedReportType.label}
              </div>
              <div className="text-xs text-purple-600 dark:text-purple-400 mt-1 transition-colors duration-300">
                {selectedReportType.description}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Process Button */}
      <div className="text-center mb-6">
        <button
          onClick={handleProcess}
          disabled={isProcessing || selectedFiles.length === 0}
          className={`font-medium py-3 px-8 rounded-lg inline-flex items-center ${
            isProcessing || selectedFiles.length === 0
              ? 'bg-gray-400 text-white cursor-not-allowed' 
              : 'bg-purple-600 hover:bg-purple-700 text-white'
          }`}
        >
          <i className={`fa-solid ${isProcessing ? 'fa-spinner fa-spin' : 'fa-search'} mr-2`}></i>
          {isProcessing ? 'Analyzing...' : 'Start Analysis'}
        </button>
      </div>

      {/* Status Message */}
      {uploadStatus && (
        <div className={`mb-6 p-4 rounded-lg text-sm transition-colors duration-300 ${
          uploadStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
          uploadStatus.type === 'error' ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
          'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
        }`}>
          {uploadStatus.message}
        </div>
      )}

      {/* Processing Steps */}
      {processingSteps.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 overflow-hidden transition-colors duration-300">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-lg font-medium text-gray-800 dark:text-white flex items-center transition-colors duration-300">
              <i className="fa-solid fa-cogs mr-2 text-purple-600 dark:text-purple-400"></i>
              Analysis Pipeline
            </h4>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {processingSteps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <i className={`fa-solid ${getStepIcon(step.status)} mr-3`}></i>
                  <span className={`text-sm transition-colors duration-300 ${
                    step.status === 'completed' ? 'text-green-700 dark:text-green-300' :
                    step.status === 'processing' ? 'text-blue-700 dark:text-blue-300' :
                    step.status === 'error' ? 'text-red-700 dark:text-red-300' :
                    'text-gray-500 dark:text-gray-400'
                  }`}>
                    {step.name}
                  </span>
                  {step.status === 'processing' && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 animate-pulse transition-colors duration-300">
                      Processing...
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AuditPlanUploadSection;
